import React from 'react';
import PropTypes from 'prop-types';
import { ComponentPage, DemoCard } from '../../shared';
import { TypographyExample, useTokenValue } from './tokenHelpers';

/**
 * The brand's actual font family, read from the cascade.
 *
 * The old page hardcoded these as prose ("Neue Haas Unica W1G (UI & Editorial)")
 * on a brand info card. Reading the variable instead means switching brand
 * updates it, and a change in fonts.config.json can never leave it lying.
 */
const Typeface = ({ label, variable, sample }) => {
  const family = useTokenValue(variable);
  return (
    <div className="flex flex-col gap-[var(--space-ui-small-m)]">
      <span className="typo-label text-[var(--ds-doc-ink-muted)]">{label}</span>
      <span className="typo-h2" style={{ fontFamily: `var(${variable})` }}>{sample}</span>
      <code className="typo-body-sm text-[var(--ds-doc-ink)] break-all">
        {variable}: {family || '—'}
      </code>
    </div>
  );
};

Typeface.propTypes = {
  /** Human-readable role of this family */
  label: PropTypes.string.isRequired,
  /** CSS custom property holding the family */
  variable: PropTypes.string.isRequired,
  /** Text rendered in the family itself */
  sample: PropTypes.string.isRequired,
};

export const meta = {
  title: 'Typography',
  group: 'Foundations',
  description:
    'Every type style in the system, as utility classes. Sizes and line heights are responsive — they switch at 1024px — and the family swaps per brand, so the same class renders CLS, ABC and Overall correctly without a single override.',
  usage: [
    {
      title: 'Applying a style',
      code: `// Type is a class, never inline CSS or a Tailwind text-* utility
<h1 className="typo-h1-editorial">Studieturstrøjer</h1>
<p className="typo-body">Body copy in the UI family.</p>
<span className="typo-label">FIELD LABEL</span>

// Each class also has a legacy .font-* alias — prefer typo-*
<h1 className="font-titleh1-editorial">…</h1>`,
    },
    {
      title: 'Where it comes from',
      code: `// Figma Text Styles → src/figma-export/MCP Version-variables.json
//   (every style's bound variables, all brand modes)
//   ↓ npm run build:tokens
// src/styles/generated/typography.css   ← the typo-* classes
//
// Never hand-edit the generated file. Change the Figma
// export and regenerate:
npm run build:tokens`,
    },
  ],
};

export default function TypographyPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Typefaces" hint="both swap with data-brand">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--space-ui-medium-l)]">
          <Typeface label="Editorial" variable="--font-editorial" sample="Studieturstrøjer" />
          <Typeface label="UI" variable="--font-ui" sample="Studieturstrøjer" />
        </div>
      </DemoCard>

      <DemoCard title="Editorial headings" hint="var(--font-editorial)">
        <div className="space-y-[var(--space-ui-medium-m)]">
          <TypographyExample className="typo-h1-editorial" alias="typo-h1-editorial" label="H1 Editorial" fontType="editorial">
            First Level Heading
          </TypographyExample>
          <TypographyExample className="typo-h2-editorial" alias="typo-h2-editorial" label="H2 Editorial" fontType="editorial">
            Second Level Heading
          </TypographyExample>
        </div>
      </DemoCard>

      <DemoCard title="UI headings" hint="var(--font-ui)">
        <div className="space-y-[var(--space-ui-medium-m)]">
          <TypographyExample className="typo-h1" alias="typo-h1" label="H1">Heading 1 — UI Style</TypographyExample>
          <TypographyExample className="typo-h2" alias="typo-h2" label="H2">Heading 2 — UI Style</TypographyExample>
          <TypographyExample className="typo-h3" alias="typo-h3" label="H3">Heading 3 — UI Style</TypographyExample>
          <TypographyExample className="typo-h4" alias="typo-h4" label="H4">Heading 4 — UI Style</TypographyExample>
          <TypographyExample className="typo-h5" alias="typo-h5" label="H5">HEADING 5 — UI STYLE</TypographyExample>
          <TypographyExample className="typo-h6" alias="typo-h6" label="H6">HEADING 6 — UI STYLE</TypographyExample>
        </div>
      </DemoCard>

      <DemoCard title="Paragraphs">
        <div className="space-y-[var(--space-ui-medium-m)]">
          <TypographyExample className="typo-body-editorial-lg" alias="typo-body-editorial-lg" label="Editorial Large" fontType="editorial">
            Large editorial paragraph for hero sections and impactful statements.
          </TypographyExample>
          <TypographyExample className="typo-body-editorial" alias="typo-body-editorial" label="Editorial Regular" fontType="editorial">
            This is an example of editorial text for articles, blogs, and long descriptions.
          </TypographyExample>
          <TypographyExample className="typo-body-lg" alias="typo-body-lg" label="Paragraph Large">
            Enlarged paragraph for accent text blocks and important information.
          </TypographyExample>
          <TypographyExample className="typo-body" alias="typo-body" label="Paragraph Regular">
            Main interface text. Used for descriptions, instructions, and primary page content.
          </TypographyExample>
          <TypographyExample className="typo-body-sm" alias="typo-body-sm" label="Paragraph Small">
            Small text for secondary information, captions, and additional explanations.
          </TypographyExample>
          <TypographyExample className="typo-label" alias="typo-label" label="Label">
            Text for labels and form fields
          </TypographyExample>
        </div>
      </DemoCard>

      <DemoCard title="Links">
        <div className="space-y-[var(--space-ui-medium-m)]">
          <TypographyExample className="typo-link-editorial-lg" alias="typo-link-editorial-lg" label="Link Editorial Large" fontType="editorial">
            Editorial link, large
          </TypographyExample>
          <TypographyExample className="typo-link-editorial" alias="typo-link-editorial" label="Link Editorial" fontType="editorial">
            Editorial link, regular
          </TypographyExample>
          <TypographyExample className="typo-link-lg" alias="typo-link-lg" label="Link Large">Link, large</TypographyExample>
          <TypographyExample className="typo-link" alias="typo-link" label="Link Regular">Link, regular</TypographyExample>
          <TypographyExample className="typo-link-sm" alias="typo-link-sm" label="Link Small">Link, small</TypographyExample>
        </div>
      </DemoCard>

      <DemoCard title="Buttons">
        <div className="space-y-[var(--space-ui-medium-m)]">
          <TypographyExample className="typo-btn-primary" alias="typo-btn-primary" label="Button Primary">Primary button text</TypographyExample>
          <TypographyExample className="typo-btn-secondary" alias="typo-btn-secondary" label="Button Secondary">SECONDARY BUTTON TEXT</TypographyExample>
          <TypographyExample className="typo-btn-tertiary" alias="typo-btn-tertiary" label="Button Tertiary">TERTIARY BUTTON TEXT</TypographyExample>
        </div>
      </DemoCard>

      <DemoCard title="Inputs">
        <div className="space-y-[var(--space-ui-medium-m)]">
          <TypographyExample className="typo-input" alias="typo-input" label="Input Value">Input field value</TypographyExample>
          <TypographyExample className="typo-input-placeholder" alias="typo-input-placeholder" label="Input Placeholder">Input field placeholder</TypographyExample>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
